package com.arena_web.ui.sections.profile.mobile

import androidx.compose.runtime.*
import com.arena_web.data.dto.CreateUserStoryRequest
import com.arena_web.data.dto.UserStoryDto
import com.arena_web.domain.repositories.UserStoryRepository
import com.arena_web.ui.components.feed.createObjectUrl
import com.arena_web.ui.components.feed.pickImageFile
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.components.widgets.GradientBorderBox
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.localization.uploadErrorMessage
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Img

// 30 MB per Figma "max 30 MB" hint — mirrors CreateStoryPopup.MAX_STORY_BYTES.
private const val MAX_STORY_BYTES = 30 * 1024 * 1024

/**
 * Mobile "Create Stories" bottom sheet — Figma 8218:322321. The phone
 * counterpart to the desktop [com.arena_web.ui.components.feed.CreateStoryPopup]:
 * same pick → upload → createStory flow, re-laid-out as a bottom sheet (drag
 * handle / swipe-to-dismiss / scrim come from the shared [BottomSheet]).
 *
 * Differences from the desktop popup, per Figma: a smaller 190×320 dashed drop
 * zone and two equal-width action buttons (Cancel + Create Stories) that fill
 * the sheet width. `contentGap = 40` matches the Figma header → body stack.
 */
@Composable
fun MobileCreateStorySheet(
    storyRepository: UserStoryRepository,
    onDismiss: () -> Unit,
    onCreated: (UserStoryDto) -> Unit,
) {
    val t = LocalAppStrings.current
    var fileBytes by remember { mutableStateOf<ByteArray?>(null) }
    var fileName by remember { mutableStateOf<String?>(null) }
    var contentType by remember { mutableStateOf<String?>(null) }
    var previewUrl by remember { mutableStateOf<String?>(null) }
    var submitting by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    val submit: () -> Unit = submit@{
        val bytes = fileBytes; val name = fileName; val mime = contentType
        if (bytes == null || name == null || mime == null) {
            error = t.profileCreateStoryPickPhotoFirst
            return@submit
        }
        submitting = true
        error = null
        scope.launch {
            val upload = storyRepository.uploadImage(bytes, name, mime)
            val url = upload.getOrNull()?.imageUrl
            if (upload.isFailure || url == null) {
                error = uploadErrorMessage(upload.exceptionOrNull(), t, t.profileCreateStoryUploadFailed)
                submitting = false
                return@launch
            }
            val create = storyRepository.createStory(CreateUserStoryRequest(imageUrl = url))
            val story = create.getOrNull()
            if (create.isFailure || story == null) {
                error = uploadErrorMessage(create.exceptionOrNull(), t, t.profileCreateStoryCreateFailed)
                submitting = false
                return@launch
            }
            onCreated(story)
            onDismiss()
        }
    }

    BottomSheet(onDismiss = onDismiss, contentGap = 40) {
        // Header (bordered bottom, gap-6 in Figma — we keep the desktop's gap-16
        // rhythm scaled down to match the tighter mobile baseline).
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sGap(6)
                .sPaddingBottom(12)
                .styleModifier { property("border-bottom", "1px solid rgba(148, 149, 152, 0)") }
        ) {
            SpanText(
                t.profileCreateStoryTitle,
                modifier = Modifier
                    .sFontSize(20)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(26)
                    .sLetterSpacing(0.2)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
            SpanText(
                t.profileCreateStorySubtitle,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(24)
                    .sLetterSpacing(0.16)
                    .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
            )
        }

        // Body: drop zone + action buttons (gap-32, centered).
        Column(
            modifier = Modifier.fillMaxWidth().sGap(32),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            StoryDropZone(
                previewUrl = previewUrl,
                onPick = {
                    pickImageFile(MAX_STORY_BYTES, onTooLarge = { error = t.profileCreateStoryFileTooLarge }) { bytes, name, mime ->
                        fileBytes = bytes
                        fileName = name
                        contentType = mime
                        previewUrl = createObjectUrl(bytes, mime)
                        error = null
                    }
                },
                onClear = {
                    fileBytes = null
                    fileName = null
                    contentType = null
                    previewUrl = null
                    error = null
                }
            )
            if (error != null) {
                SpanText(
                    error!!,
                    modifier = Modifier.sFontSize(12).color(AppColors.Error)
                )
            }
            // Action buttons — two equal halves filling the form width (gap-12).
            Row(
                modifier = Modifier.fillMaxWidth().sGap(12),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically
            ) {
                ActionButton(
                    text = t.commonCancel,
                    primary = false,
                    enabled = !submitting,
                    onClick = onDismiss,
                )
                ActionButton(
                    text = if (submitting) t.profileCreateStoryCreating else t.profileCreateStoryTitle,
                    primary = true,
                    enabled = !submitting && fileBytes != null,
                    onClick = submit,
                )
            }
        }
    }
}

@Composable
private fun StoryDropZone(
    previewUrl: String?,
    onPick: () -> Unit,
    onClear: () -> Unit,
) {
    val t = LocalAppStrings.current
    // Figma 8218:322327 — 190×320, dashed 2px #0c1787, rounded-22.
    Box(
        modifier = Modifier
            .sWidth(190)
            .sHeight(320)
            .sBorderRadius(22)
            .cursor(Cursor.Pointer)
            .onClick { if (previewUrl == null) onPick() }
            .styleModifier {
                property("position", "relative")
                property("overflow", "hidden")
                if (previewUrl == null) {
                    property("border", "2px dashed #0c1787")
                    property(
                        "background",
                        "linear-gradient(228.99deg, rgba(60, 121, 241, 0.06) 49.48%, rgba(5, 4, 12, 0.06) 105.9%)"
                    )
                } else {
                    property("border", "2px solid #0c1787")
                }
            },
        contentAlignment = Alignment.Center
    ) {
        if (previewUrl == null) {
            Column(
                modifier = Modifier.sGap(10),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Column(
                    modifier = Modifier.sGap(16),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    // 48-circle "+" — InteractiveSecondary fill + ButtonBorderGradientPrimary
                    // hairline via mask-composite (same as the desktop drop zone).
                    Box(
                        modifier = Modifier
                            .sSize(48)
                            .sBorderRadius(100)
                            .styleModifier {
                                property("background", AppGradients.InteractiveSecondary)
                                property("position", "relative")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        Box(
                            modifier = Modifier.fillMaxSize().styleModifier {
                                property("position", "absolute")
                                property("inset", "0")
                                property("border", "0.3px solid transparent")
                                property("border-radius", s(100))
                                property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                                property("-webkit-mask-composite", "xor")
                                property("mask-composite", "exclude")
                                property("pointer-events", "none")
                            }
                        )
                        Icon(src = AppIcons.AddSecondary, modifier = Modifier.sSize(20))
                    }
                    SpanText(
                        t.profileCreateStoryDropHere,
                        modifier = Modifier
                            .sFontSize(14)
                            .fontWeight(AppTypography.Regular)
                            .sLineHeight(22)
                            .sLetterSpacing(0.028)
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
                SpanText(
                    t.profileCreateStoryMaxSize,
                    modifier = Modifier
                        .sFontSize(12)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(12)
                        .sLetterSpacing(0.012)
                        .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                )
            }
        } else {
            Img(
                src = previewUrl,
                attrs = Modifier
                    .fillMaxSize()
                    .sBorderRadius(20)
                    .styleModifier { property("object-fit", "cover") }
                    .toAttrs()
            )
            // Remove button (top-right).
            Box(
                modifier = Modifier
                    .sSize(28)
                    .sBorderRadius(100)
                    .cursor(Cursor.Pointer)
                    .styleModifier {
                        property("position", "absolute")
                        property("top", s(8))
                        property("right", s(8))
                        property("background", "rgba(0, 0, 0, 0.6)")
                        property("border", "0.3px solid var(--arena-border-top)")
                    }
                    .onClick {
                        it.stopPropagation()
                        onClear()
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    "×",
                    modifier = Modifier.sFontSize(16).color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }
        }
    }
}

/**
 * Equal-width pill (Figma 8220:322369/322370 — fixed 169 each, here flex-1 so
 * the pair fills the form width with the gap between them). Primary = solid
 * sport fill; secondary "Cancel" = InteractiveSecondary fill. Both carry the
 * ButtonBorderGradientPrimary hairline.
 */
@Composable
private fun ActionButton(
    text: String,
    primary: Boolean,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    val sportColors = LocalSportColors.current
    val label: @Composable () -> Unit = {
        SpanText(
            text,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(18)
                .sLetterSpacing(0.032)
                .color(if (primary) org.jetbrains.compose.web.css.Color(AppColors.Light.Background.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
        )
    }
    if (primary) {
        val fill = sportColors.primary.toRgb()
        val fillCss = "rgb(${fill.red}, ${fill.green}, ${fill.blue})"
        Box(
            modifier = Modifier
                .flexGrow(1)
                .sHeight(44)
                .sBorderRadius(100)
                .cursor(if (enabled) Cursor.Pointer else Cursor.Default)
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    // Solid sport fill (padding-box) + gradient hairline (border-box).
                    // The opaque fill keeps the border ring from bleeding inward.
                    property(
                        "background",
                        "linear-gradient($fillCss, $fillCss) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                    property("opacity", if (enabled) "1" else "0.5")
                }
                .onClick { if (enabled) onClick() },
            contentAlignment = Alignment.Center
        ) { label() }
    } else {
        // Secondary "Cancel" — translucent InteractiveSecondary fill with the
        // ButtonBorderGradientPrimary hairline drawn as a mask-composite ring
        // (GradientBorderBox), the project's proven outline-pill recipe. A flat
        // border-box paint here let the gradient bleed through the translucent
        // fill and tinted the whole button; the isolated ring fixes that.
        GradientBorderBox(
            modifier = Modifier
                .flexGrow(1)
                .sHeight(44)
                .cursor(if (enabled) Cursor.Pointer else Cursor.Default)
                .styleModifier { property("opacity", if (enabled) "1" else "0.5") }
                .onClick { if (enabled) onClick() },
            borderRadius = 100,
            background = AppGradients.InteractiveSecondary,
            borderWidth = "0.3px",
        ) { label() }
    }
}
